<script setup>
import {ref, computed, onMounted, reactive} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import LoadingContent from "../components/LoadingContent.vue";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import {camelCase, scrollToTop} from "../../../../../helpers/common";


const portalContentAndDesignStore = usePortalContentAndDesignStore();

const layoutStyle = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style);
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners);

const isSuspended = computed(() => portalContentAndDesignStore.partner?.status === 'suspended' || false);

onMounted(() => {
    portalContentAndDesignStore.initializePortalPageContent('affiliate-profile')
        .then(() => {
            form.name = portalContentAndDesignStore.authUser.name || '';
            form.surname = portalContentAndDesignStore.authUser.surname || '';
            form.email = portalContentAndDesignStore.authUser.email || '';
            form.email_notification_about_new_signups = portalContentAndDesignStore.profilePageSettings.email_notification_about_new_signups || false;
            form.email_notification_about_sales = portalContentAndDesignStore.profilePageSettings.email_notification_about_sales || false;
            form.email_notification_about_payout_requests = portalContentAndDesignStore.profilePageSettings.email_notification_about_payout_requests || false;
        })
})

const form = reactive({
        profile_photo: '',
        name: '',
        surname: '',
        accepts_marketing: '',
        email: '',
        password_placeholder: '',
        password: '',
        new_password: '',
        new_password_confirmation: '',
        email_notification_about_new_signups: '',
        email_notification_about_sales: '',
        email_notification_about_payout_requests: '',
})

const formErrors = ref({});

const emailFieldVisible = ref(false);
const emailHasError = computed(() => !!formErrors.value.email);
const passwordHasErrors = computed(() =>
    !!formErrors.value.password
    || !!formErrors.value.new_password
    || !!formErrors.value.new_password_confirmation
);

function toggleEmailField() {
    emailFieldVisible.value = true;
}

const updating = ref(false);

const successUpdateMessage = ref(null);

function updateProfile() {
    formErrors.value = {};
    successUpdateMessage.value = null;
    updating.value = true;

    axios.post(route('partners_int.affiliate-profile.update'), form)
        .then(response => {
            const status = parseInt(response.data.status || 0);
            if (status === 1) {
                successUpdateMessage.value = response.data.message || 'Profile updated successfully.';
                portalContentAndDesignStore.onboardingStep = response.data.onboarding_step || 3;
                scrollToTop()
            } else if (status === 0) {
                formErrors.value = response.data.errors;
            }
        })
        .catch(error => {
            if (error.response.status === 422) {
                formErrors.value = error.response.data.errors;
            } else {
                formErrors.value = {general: 'An error occurred. Please try again.'};
                console.error(error);
            }
        })
        .finally(() => {
            updating.value = false;
        });
}
</script>

<template>
    <div v-if="Object.keys(portalContentAndDesignStore.profilePageSettings).length > 0">
        <!-- Suspended Notice -->
        <div
            v-if="isSuspended"
            :class="[
                layoutStyle === 'none' ? 'bg-gray-600 bg-opacity-5' : 'bg-white',
                corners,
                layoutStyle === 'border' ? 'border border-gray-200 border-opacity-75' : layoutStyle
            ]"
        >
            <div class="px-8">
                <div class="flex justify-between items-center py-14">
                  <span>
                    Your account was suspended.
                    <template v-if="portalContentAndDesignStore.profilePageSettings.suspend_reason">
                        Reason: {{ portalContentAndDesignStore.profilePageSettings.suspend_reason }}
                    </template>
                  </span>
                </div>
            </div>
        </div>

        <!-- Profile Form Wrapper -->
        <div
            class="mb-10"
            :class="[
                layoutStyle !== 'none' ? 'bg-white' : '',
                corners,
                layoutStyle === 'border' ? 'border border-gray-200 border-opacity-75' : layoutStyle
            ]"
        >
            <div v-if="successUpdateMessage"
                 class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-2"
                 role="alert"
            >
                <span class="block sm:inline">{{ successUpdateMessage }}</span>
                <span class="absolute top-0 bottom-0 right-0 px-4 py-3"></span>
            </div>
            <div class="divide-y divide-gray-200" :class="layoutStyle !== 'none' ? 'px-10' : ''">
                <!-- Personal Info Section -->
                <div class="grid py-14 xl:grid-cols-3 gap-10">
                    <div class="space-y-2">
                        <editable-text-field
                            text-type="h3"
                            class="text-lg font-semibold"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_personal_info"
                        />
                    </div>

                    <div class="grid md:grid-cols-2 col-span-2 gap-8">
                        <file-upload
                            :src="portalContentAndDesignStore.srcRoutes.profile_photo"
                            no-src="https://placehold.co/100x100/E5E7EB/9CA3AF?text=avatar"
                            accept="image/*"
                            :title="portalContentAndDesignStore.translationSettings.portal_translation_profile_profile_picture || 'Profile picture'"
                            name="profile_photo"
                            :upload_url="portalContentAndDesignStore.getProfileAvatarUploadRoute"
                            :delete_url="portalContentAndDesignStore.getProfileAvatarDeleteRoute"
                        />

                        <div></div>

                        <div class="space-y-1.5">
                            <editable-text-field
                                text-type="label"
                                for-attribute="name"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_first_name"
                            />
                            <text-field
                                fieldId="name"
                                :error="(formErrors.name ?? false)"
                                v-model="form.name"
                            />
                        </div>

                        <div class="space-y-1.5">
                            <editable-text-field
                                text-type="label"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_last_name"
                            />
                            <text-field
                                fieldId="surname"
                                :error="(formErrors.surname ?? false)"
                                v-model="form.surname"
                            />
                        </div>

                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="accepts_marketing"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.accepts_marketing"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_accept_marketing"
                                />
                            </span>
                        </div>
                    </div>
                </div>
                <!-- Login Details -->
                <div class="grid py-14 xl:grid-cols-3 gap-10">
                    <editable-text-field
                        text-type="h3"
                        class="text-lg font-semibold"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_login_details"
                    />

                    <div class="grid col-span-2 gap-8">
                        <div v-if="!emailHasError && !emailFieldVisible" class="flex justify-between items-end gap-4">
                            <div class="space-y-1.5 w-full">
                                <editable-text-field
                                    text-type="label"
                                    for-attribute="email"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_email_address"
                                />
                                <text-field
                                    disabled
                                    :model-value="form.email"
                                    class="w-full"
                                />
                            </div>
                            <editable-btn custom-class="flex-shrink-0 whitespace-nowrap"
                                          type="light"
                                          @click="toggleEmailField"
                                          v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_change_email"
                            />
                        </div>

                        <div v-if="emailHasError || emailFieldVisible">
                            <div class="space-y-1.5">
                                <editable-text-field
                                    text-type="label"
                                    for-attribute="email"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_email_address"
                                />
                                <text-field
                                    type="email"
                                    field-id="email"
                                    v-model="form.email"
                                    :error="!!formErrors.email"
                                    :error-message="formErrors.email"
                                />
                            </div>
                        </div>

                        <password
                            v-if="!portalContentAndDesignStore.profilePageSettings.portal_affiliate_enable_partners_to_login_signup_via_otp"
                            v-slot="{ childScope, childMethods }"
                            :min="portalContentAndDesignStore.profilePageSettings.password_validation_rules.min"
                            :regex="portalContentAndDesignStore.profilePageSettings.password_validation_rules.regex"
                            :errors="passwordHasErrors"
                            v-cloak
                        >
                            <div v-if="!childScope.passwordFormVisible" class="flex justify-between items-end gap-4">
                                <div class="space-y-1.5 w-full">
                                    <editable-text-field
                                        text-type="label"
                                        for-attribute="password_placeholder"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_password"
                                    />
                                    <text-field
                                        type="password"
                                        name="password_placeholder"
                                        field-id="password_placeholder"
                                        disabled
                                        model-value="'********'"
                                        class="w-full"
                                    />
                                </div>
                                <editable-btn class="flex-shrink-0 whitespace-nowrap"
                                              type="light"
                                              @click="childMethods.showPasswordForm"
                                              v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_change_password"
                                />
                            </div>

                            <div v-if="childScope.passwordFormVisible">
                                <div class="mb-5">
                                    <div class="space-y-1.5">
                                        <editable-text-field
                                            text-type="label"
                                            for-attribute="password"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_current_password"
                                        />
                                        <text-field
                                            type="password"
                                            name="password"
                                            field-id="password"
                                            :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_enter_current_password || 'Enter your current password'"
                                            v-model="form.password"
                                            :error="!!formErrors.password"
                                            :error-message="formErrors.password"
                                        />
                                    </div>
                                </div>

                                <div class="mb-5">
                                    <div class="space-y-1.5">
                                        <editable-text-field
                                            text-type="label"
                                            for-attribute="new_password"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password"
                                        />
                                        <text-field
                                            v-model="childScope.password"
                                            @update:model-value="form.new_password = $event"
                                            type="password"
                                            name="new_password"
                                            field-id="new_password"
                                            :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password || 'New password'"
                                            :error="!!formErrors.new_password"
                                            :error-message="formErrors.new_password"
                                        />
                                    </div>

                                    <div class="grid grid-cols-2 gap-2 mt-5">
                                        <template v-for="(text, regex) in portalContentAndDesignStore.profilePageSettings.password_validation_rules.regex" :key="regex">
                                            <div class="flex items-center">
                                                <div
                                                    class="h-2 w-2 rounded-full mr-2"
                                                    :class="[childScope.rules[camelCase(text)] ? 'bg-black' : 'bg-gray-400']"
                                                ></div>
                                                <editable-text-field
                                                    v-if="regex === '[a-z]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_lowercase_character"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                                <editable-text-field
                                                    v-else-if="regex === '[0-9]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_number"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                                <editable-text-field
                                                    v-else-if="regex === '[A-Z]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_uppercase_character"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                            </div>
                                        </template>

                                        <div class="flex items-center">
                                            <div
                                                class="h-2 w-2 rounded-full mr-2"
                                                :class="[childScope.rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                                            ></div>
                                            <editable-text-field
                                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_characters_minimum"
                                                :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                text-type="span"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="space-y-1.5">
                                    <editable-text-field
                                        text-type="label"
                                        for-attribute="new_password_confirmation"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_confirm_password"
                                    />
                                    <text-field
                                        type="password"
                                        name="new_password_confirmation"
                                        field-id="new_password_confirmation"
                                        v-model="form.new_password_confirmation"
                                        :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password || 'New password'"
                                        :error="!!formErrors.new_password_confirmation"
                                        :error-message="formErrors.new_password_confirmation"
                                    />
                                </div>
                            </div>
                        </password>
                    </div>
                </div>
                <!-- Notifications -->
                <div v-if="portalContentAndDesignStore.profilePageSettings.allow_partner_email_notifications"
                     class="grid py-14 xl:grid-cols-3 gap-10"
                >
                    <div class="space-y-2">
                        <editable-text-field
                            text-type="h3"
                            class="text-lg font-semibold"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notifications"
                        />
                        <editable-text-field
                            class="text-sm opacity-60"
                            text-type="p"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_explanation"
                        />
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_new_signups"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_new_signups"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_signups"
                                />
                            </span>
                        </div>

                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_sales"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_sales"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_sales"
                                />
                            </span>
                        </div>

                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_payout_requests"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_payout_requests"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_payouts"
                                />
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="false" class="flex justify-between items-center py-14">
                    <div>
                        <h3 class="text-lg font-semibold">
                            Two-Factor Authentication
                        </h3>
                        <p class="text-sm opacity-60">
                            It is a security process where the user provides two forms of ID to log in to Partnero.
                            This
                            helps keep your account safe.
                        </p>
                    </div>
                    <div>
                        <btn type="light">Enable</btn>
                    </div>
                </div>

                <div class="py-6 text-right">
                    <editable-btn
                        @click="updateProfile"
                        :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_update"
                        :loading="updating"
                        :disabled="updating"
                    />
                </div>
            </div>
        </div>

        <!-- Delete Account -->
        <div
            v-if="!portalContentAndDesignStore.profilePageSettings.show_onboarding"
            :class="[
                layoutStyle === 'none' ? 'bg-gray-600 bg-opacity-5' : 'bg-white',
                corners,
                layoutStyle === 'border' ? 'border border-gray-200 border-opacity-75' : layoutStyle
            ]"
        >
            <div class="px-8">
                <div class="flex justify-between items-center py-14">
                    <partner-delete
                        :delete_url="portalContentAndDesignStore.getDeleteAccountRoute"
                        :translations="[]"
                    />
                </div>
            </div>
        </div>
    </div>
    <loading-content v-else />
</template>
